import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { CsPage, NavController } from 'providers/nav-controller';
import { confirmDiscardAsync } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { AlertController } from 'providers/alert-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { SettingsService } from 'services/settings.service';
import { formatMeasure } from 'util/formatting';
import { EditNutritionPlanPageParams, EditNutritionPlanPageResult } from './edit-nutrition-plan-interface';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { KCAL_PER_G_PROTEIN, KCAL_PER_G_FAT, KCAL_PER_G_CARBS, getMinimumDailyCarbs_g, getMinimumDailyFat_g, splitRemainingKcalByCarbRatio, carbFatRatioPercentFromGrams } from '@cf-shared/data-model/food';
import { Gender } from '@cf-shared/enums';

import { CsSliderBackgroundGradientMark, CsSliderTickmark } from 'components/cs-slider/cs-slider';

import { LL } from 'i18n/i18n.service';
@Component({
    selector: 'page-edit-nutrition-plan',
    templateUrl: 'edit-nutrition-plan.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
  providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 480 }) }]
})
export class EditNutritionPlanPage extends CsPage<EditNutritionPlanPageParams> {
  protected LL = LL;

  dailyIntake: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;

  /** carb percentage of remaining kcal (20-80 for slider) */
  carbFatRatio: number = 55;
  autoAdjust: boolean = true;
  isFlexible: boolean = false;

  recommendedDailyIntake: number;
  recommendedProtein: number;
  recommendedCarbs: number;
  recommendedFat: number;
  recommendedFiber: number;

  // slider config
  carbFatTickmarks: CsSliderTickmark[] = [];
  carbFatGradientMarks: CsSliderBackgroundGradientMark[] = [];
  carbFatHighlightedValues: number[] = [];

  private gender: Gender;
  private manualCarbFatEdit: boolean = false;
  private skipInitialSliderEmit: boolean = true;
  /** The numbers this page opened with, so the guard can tell an edit from a load. */
  private planAsLoaded = { dailyIntake: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, autoAdjust: false, carbFatRatio: 0 };
  private recommendedCarbFatRatio: number = 55;

  /** threshold for calorie mismatch warning (kcal) */
  private readonly MISMATCH_THRESHOLD = 50;

  constructor(
    public navCtrl: NavController,
    private settings: SettingsService,
    private alertCtrl: AlertController,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  async ngOnInit() {
    const data = this.navParams.data.local;
    this.gender = data?.gender;
    const gender = this.gender;
    this.isFlexible = data?.isFlexible ?? false;

    // recommended values from solver (pre-computed by caller without overrides)
    this.recommendedDailyIntake = data?.recommendedDailyIntake ?? 0;
    this.recommendedProtein = data?.recommendedProtein ?? 0;
    this.recommendedCarbs = data?.recommendedCarbs ?? 0;
    this.recommendedFat = data?.recommendedFat ?? 0;
    this.recommendedFiber = data?.recommendedFiber ?? 0;

    // current values (pre-computed by caller with overrides applied)
    this.dailyIntake = data?.dailyIntake ?? this.recommendedDailyIntake;
    this.protein = data?.protein ?? this.recommendedProtein;
    this.carbs = data?.carbs ?? this.recommendedCarbs;
    this.fat = data?.fat ?? this.recommendedFat;
    this.fiber = data?.fiber ?? this.recommendedFiber;

    if (!this.isFlexible) {
      // fixed mode: restore ratio and autoAdjust from existing override state
      this.autoAdjust = data?.autoAdjust ?? true;
      if (data?.carbFatRatio != null) {
        this.carbFatRatio = Math.round(data.carbFatRatio * 100);
      } else {
        this.carbFatRatio = this.computeCarbFatRatioFromGrams();
      }
    }

    // slider configuration (fixed mode only)
    if (!this.isFlexible) {
      // compute recommended ratio from recommended macro grams
      this.recommendedCarbFatRatio = carbFatRatioPercentFromGrams(this.recommendedCarbs, this.recommendedFat);

      // highlighted value at the recommended ratio
      this.carbFatHighlightedValues = [this.recommendedCarbFatRatio];

      // color zones: green in safe range, yellow buffer, red at extremes
      const remainingKcal = Math.max(1, this.dailyIntake - this.recommendedProtein * KCAL_PER_G_PROTEIN);
      const minCarbRatio = Math.round((getMinimumDailyCarbs_g(gender) * KCAL_PER_G_CARBS / remainingKcal) * 100);
      const minFatRatio = Math.round((getMinimumDailyFat_g(this.dailyIntake) * KCAL_PER_G_FAT / remainingKcal) * 100);
      const maxCarbRatio = 100 - minFatRatio;
      const greenLeft = Math.max(20, minCarbRatio);
      const greenRight = Math.min(80, maxCarbRatio);
      this.carbFatGradientMarks = [
        { value: 20, color: 'red' },
        { value: greenLeft - 5, color: 'yellow' },
        { value: greenLeft + 5, color: 'green' },
        { value: greenRight - 5, color: 'green' },
        { value: greenRight + 5, color: 'yellow' },
        { value: 80, color: 'red' },
      ];
      this.carbFatTickmarks = [
        { value: 20, label: '20%' },
        { value: greenLeft, label: `${greenLeft}%` },
        { value: greenRight, label: `${greenRight}%` },
        { value: 80, label: '80%' },
      ];
    }

    // Snapshot only NOW, once every field is filled in, so a page that merely
    // opened does not look edited. Taking it earlier is the mistake push 1 made
    // on Edit Food, where a baseline caught before the page finished setting
    // itself up made every untouched food raise the dialog.
    this.planAsLoaded = {
      dailyIntake: this.dailyIntake,
      protein: this.protein,
      carbs: this.carbs,
      fat: this.fat,
      fiber: this.fiber,
      autoAdjust: this.autoAdjust,
      carbFatRatio: this.carbFatRatio,
    };
  }

  getFormattedRecommendedIntake(): string {
    return formatMeasure(Math.round(this.recommendedDailyIntake), 'nutrient_kcal', this.settings.locale, 'kcal');
  }

  getFormattedRecommendedMacro(grams: number): string {
    return formatMeasure(Math.round(grams), 'nutrient_g', this.settings.locale, 'g');
  }

  getCarbsCaloriesFormatted(): string {
    return formatMeasure(Math.round((this.carbs || 0) * KCAL_PER_G_CARBS), 'nutrient_kcal', this.settings.locale, 'kcal');
  }

  getFatCaloriesFormatted(): string {
    return formatMeasure(Math.round((this.fat || 0) * KCAL_PER_G_FAT), 'nutrient_kcal', this.settings.locale, 'kcal');
  }

  // -- calorie changes --

  onCaloriesChange() {
    if (!this.isFlexible && this.autoAdjust) {
      this.recalculateMacrosFromRatio();
    }
    this.cd.markForCheck();
  }

  // -- macro changes (flexible mode individual inputs, or protein/fiber in fixed mode) --

  onMacroChange() {
    if (!this.isFlexible) {
      // in fixed mode, adjust calories to match macro sum
      this.recalculateCaloriesFromMacros();
    }
    this.cd.markForCheck();
  }

  // -- carb/fat slider (fixed mode) --

  onCarbFatRatioChange(value: number) {
    // skip the initial emit from cs-slider setting its value on init
    if (this.skipInitialSliderEmit) {
      this.skipInitialSliderEmit = false;
      return;
    }
    // if the ratio update came from a manual carb/fat input edit, skip recalculating
    if (this.manualCarbFatEdit) {
      this.manualCarbFatEdit = false;
      return;
    }
    this.carbFatRatio = value;
    this.recalculateCarbsFatFromRatio();
    this.cd.markForCheck();
  }

  // -- manual carb/fat input changes in fixed mode --

  onCarbsManualChange() {
    // keep carbs as user typed; update slider ratio and adjust calories to match
    this.manualCarbFatEdit = true;
    this.carbFatRatio = this.computeCarbFatRatioFromGrams();
    this.recalculateCaloriesFromMacros();
    this.cd.markForCheck();
  }

  onFatManualChange() {
    // keep fat as user typed; update slider ratio and adjust calories to match
    this.manualCarbFatEdit = true;
    this.carbFatRatio = this.computeCarbFatRatioFromGrams();
    this.recalculateCaloriesFromMacros();
    this.cd.markForCheck();
  }

  // -- mismatch detection --

  hasCalorieMismatch(): boolean {
    if (this.isFlexible || !this.dailyIntake) return false;
    const macroKcal = this.protein * KCAL_PER_G_PROTEIN + this.carbs * KCAL_PER_G_CARBS + this.fat * KCAL_PER_G_FAT;
    return macroKcal - this.dailyIntake > this.MISMATCH_THRESHOLD;
  }

  // -- reset --

  handleReset() {
    this.dailyIntake = this.recommendedDailyIntake;
    this.protein = this.recommendedProtein;
    this.carbs = this.recommendedCarbs;
    this.fat = this.recommendedFat;
    this.fiber = this.recommendedFiber;
    this.autoAdjust = true;
    this.carbFatRatio = this.computeCarbFatRatioFromGrams();
    this.cd.markForCheck();
  }

  // -- navigation --

  handleCancel(event: Event) {
    this.leaveWithoutAsking.set();
    lockClick(event, this.navCtrl.popAsync());
  }

  /**
   * Runs before the page closes, whichever way the user leaves: the back arrow,
   * the browser back button, Android's hardware back button or a swipe back.
   * Without it, calorie and macro numbers the user had just dialled in were
   * thrown away in silence (#5058).
   *
   * The page has no database write of its own — Save hands the numbers back to
   * the page that opened it — so there is nothing to save them into from here,
   * and the only real question is whether the user meant to drop them.
   */
  async ionViewCanLeave(): Promise<boolean> {
    // Save and Cancel arm this, having already settled what happens to the numbers
    if (this.leaveWithoutAsking.consume()) return true;
    if (!this.hasUnsavedPlanEdits()) return true;
    return confirmDiscardAsync(this.alertCtrl, {
      title: LL.unsavedChanges.discard.nutritionPlanTitle(),
      message: LL.unsavedChanges.discard.nutritionPlanMessage(),
    });
  }

  /** Whether the numbers on screen differ from the ones the page opened with. */
  private hasUnsavedPlanEdits(): boolean {
    const a = this.planAsLoaded;
    return this.dailyIntake !== a.dailyIntake
      || this.protein !== a.protein
      || this.carbs !== a.carbs
      || this.fat !== a.fat
      || this.fiber !== a.fiber
      || this.autoAdjust !== a.autoAdjust
      // Compared in its own right, not just through carbs and fat. Moving the
      // slider normally changes both of those — except when there are no
      // calories left to split between them, where every ratio yields zero grams
      // and a moved slider would otherwise look untouched.
      || this.carbFatRatio !== a.carbFatRatio;
  }

  handleSave(event: Event) {
    lockClick(event, this.handleSaveAsync());
  }

  private async handleSaveAsync() {
    this.leaveWithoutAsking.set();
    const result: EditNutritionPlanPageResult = {
      dailyIntake: this.dailyIntake,
      overrideMacros: this.buildOverrideMacros(),
    };
    await this.navCtrl.popAsync(result);
  }

  // -- private helpers --

  private buildOverrideMacros(): FitnessPlan['overrideMacros'] {
    const result: FitnessPlan['overrideMacros'] = {};

    // only store values that differ from recommended
    if (Math.round(this.protein) !== Math.round(this.recommendedProtein)) {
      result.protein_g_per_day = Math.round(this.protein);
    }
    if (Math.round(this.fiber) !== Math.round(this.recommendedFiber)) {
      result.fiber_g_per_day = Math.round(this.fiber);
    }

    if (this.isFlexible) {
      if (Math.round(this.carbs) !== Math.round(this.recommendedCarbs)) {
        result.carbs_g_per_day = Math.round(this.carbs);
      }
      if (Math.round(this.fat) !== Math.round(this.recommendedFat)) {
        result.fat_g_per_day = Math.round(this.fat);
      }
    } else {
      // fixed mode: store ratio and autoAdjust only if changed
      if (this.carbFatRatio !== this.recommendedCarbFatRatio) {
        result.carbFatRatio = this.carbFatRatio / 100;
      }
      if (!this.autoAdjust) {
        result.autoAdjust = false;
      }
    }

    // if nothing was overridden, return undefined
    return Object.keys(result).length > 0 ? result : undefined;
  }

  /** recalculate all macros from recommended ratios (auto-adjust) */
  private recalculateMacrosFromRatio() {
    // protein and fiber stay proportional to recommended (they don't scale with calories)
    this.protein = this.recommendedProtein;
    this.fiber = this.recommendedFiber;
    this.recalculateCarbsFatFromRatio();
  }

  /** recalculate calories from current macro gram values */
  private recalculateCaloriesFromMacros() {
    this.dailyIntake = Math.round(
      (this.protein || 0) * KCAL_PER_G_PROTEIN +
      (this.carbs || 0) * KCAL_PER_G_CARBS +
      (this.fat || 0) * KCAL_PER_G_FAT
    );
  }

  /** recalculate carbs and fat from current ratio slider value */
  private recalculateCarbsFatFromRatio() {
    // if dailyIntake is undefined (user cleared the field), skip recalculation
    if (this.dailyIntake == null) return;
    const split = splitRemainingKcalByCarbRatio(this.dailyIntake, this.protein, this.carbFatRatio / 100);
    this.carbs = split.carbs_g;
    this.fat = split.fat_g;
  }

  /** compute the carb/fat ratio (0-100) from current gram values */
  private computeCarbFatRatioFromGrams(): number {
    return carbFatRatioPercentFromGrams(this.carbs, this.fat);
  }
}
